import { useState } from "react";
import { Offer } from "@/types/offer";
import { toast } from "sonner";
import { isAvailabilityStatus, isSignupStatus } from "@/functions-shared/offer/offerStatusEnums";
import { bulkDeleteOffersViaAdminService } from "@/services/offer/offerBulkDeleteService";
import type { RegistrationEventFormValues } from "@/components/admin/locations/components/calendar/schemas/offerFormSchema";

// Define the interface for bulk edit form data
export interface BulkEditFormData {
  sessionTitle: {
    enabled: boolean;
    value: string;
  };
  sessionUrl: {
    enabled: boolean;
    value: string;
  };
  registrationUrl: {
    enabled: boolean;
    value: string;
  };
  signupStatus: {
    enabled: boolean;
    value: string;
  };
  availabilityStatus: {
    enabled: boolean;
    value: string;
  };
  sessionDescription: {
    enabled: boolean;
    value: string;
  };
  regularPrice: {
    enabled: boolean;
    value: string; // Will be converted to number when saving
  };
  priceNote: {
    enabled: boolean;
    value: string;
  };
  timingNote: {
    enabled: boolean;
    value: string;
  };
  currentSalePrice: {
    enabled: boolean;
    value: string; // Will be converted to number when saving
  };
  visibility: {
    enabled: boolean;
    value: string;
  };
  regEvents: {
    enabled: boolean;
    value: RegistrationEventFormValues[];
  };
}

export const useSessionsTable = (
  offers: Offer[],
  onEditOffer: (offer: Offer) => void,
  onDeleteOffer: (offerId: string) => void,
  onCopyOffer?: (offerId: string) => void,
  onBulkUpdateOffers?: (offerIds: string[], updateData: Partial<Offer>, suppressToasts?: boolean) => Promise<void>,
  onRefreshOffers?: () => Promise<void>
) => {
  const [selectedOffers, setSelectedOffers] = useState<Offer[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageSize, setPageSize] = useState("25");
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<string | null>(null);
  
  // Add state for bulk delete confirmation
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  
  // Add state for bulk edit modal
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

  // Handle shift-click selection
  const handleShiftClick = (currentIndex: number, sortedOffers: Offer[]) => {
    if (lastClickedIndex === null) {
      return [sortedOffers[currentIndex]];
    }

    const start = Math.min(currentIndex, lastClickedIndex);
    const end = Math.max(currentIndex, lastClickedIndex);
    const rangeOffers = sortedOffers.slice(start, end + 1);

    // Merge with existing selections that are outside the range
    const existingSelections = selectedOffers.filter(selected => {
      const index = sortedOffers.findIndex(offer => offer.id === selected.id);
      return index < start || index > end;
    });

    return [...existingSelections, ...rangeOffers];
  };

  // Handle row selection with shift-click support
  // checked parameter: if provided, sets selection to that state; if undefined, toggles
  const handleRowSelect = (offer: Offer, index: number, event: React.MouseEvent, sortedOffers: Offer[], checked?: boolean) => {
    // Extract checked from event if it's a custom event with __checked property
    const eventChecked = (event as any).__checked;
    const finalChecked = checked !== undefined ? checked : eventChecked;
    
    if (event.shiftKey && lastClickedIndex !== null) {
      // Shift-click: select range between last clicked and current
      setSelectedOffers(handleShiftClick(index, sortedOffers));
      // Update last clicked index to the current one for next shift-click
      setLastClickedIndex(index);
    } else {
      // Regular click: use checked parameter if provided, otherwise toggle
      if (finalChecked !== undefined) {
        // Set selection to the specified state
        if (finalChecked) {
          setSelectedOffers(prev => {
            const isSelected = prev.some(selected => selected.id === offer.id);
            return isSelected ? prev : [...prev, offer];
          });
        } else {
          setSelectedOffers(prev => prev.filter(selected => selected.id !== offer.id));
        }
      } else {
        // Toggle selection (for row clicks)
        const isSelected = selectedOffers.some(selected => selected.id === offer.id);
        if (isSelected) {
          setSelectedOffers(prev => prev.filter(selected => selected.id !== offer.id));
        } else {
          setSelectedOffers(prev => [...prev, offer]);
        }
      }
      // Always update last clicked index for shift-click support
      setLastClickedIndex(index);
    }
  };

  // Handler for delete button click
  const handleDeleteClick = (offerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOfferToDelete(offerId);
    setIsDeleteDialogOpen(true);
  };

  // Handler for confirming deletion
  const handleConfirmDelete = () => {
    if (offerToDelete) {
      onDeleteOffer(offerToDelete);
      setOfferToDelete(null);
    }
    setIsDeleteDialogOpen(false);
  };

  // Handle bulk delete - Updated to show confirmation dialog
  const handleBulkDelete = () => {
    // Open confirmation dialog instead of deleting immediately
    setIsBulkDeleteDialogOpen(true);
  };
  
  // New function to perform the actual bulk deletion after confirmation via Cloud Run admin-service
  const handleConfirmBulkDelete = async () => {
    try {
      const offerIdsToDelete = selectedOffers.map(offer => offer.id);
      const loadingToast = toast.loading(`Deleting ${offerIdsToDelete.length} sessions...`);

      const result = await bulkDeleteOffersViaAdminService(offerIdsToDelete);
      toast.dismiss(loadingToast);

      if (!result.success) {
        console.error("Bulk delete failed:", result.error);
        toast.error(result.error || "Failed to delete sessions");
        return;
      }

      toast.success(`Successfully deleted ${result.deleted ?? offerIdsToDelete.length} sessions`);
      setSelectedOffers([]);
      setIsBulkDeleteDialogOpen(false);
      if (onRefreshOffers) {
        await onRefreshOffers();
      }
    } catch (error) {
      console.error("Error bulk deleting offers:", error);
      toast.error("Failed to delete sessions");
    }
  };
  
  // Handle bulk edit
  const handleBulkEdit = () => {
    setIsBulkEditModalOpen(true);
  };
  
  // Handle bulk edit save
  const handleSaveBulkEdit = async (formData: BulkEditFormData) => {
    if (!onBulkUpdateOffers || selectedOffers.length === 0) {
      setIsBulkEditModalOpen(false);
      return;
    }
    
    
    const updateData: Partial<Offer> = {};
    
    // Only add fields that are enabled to the update data
    if (formData.sessionTitle.enabled && formData.sessionTitle.value.trim() !== "") {
      updateData.displayTitle = formData.sessionTitle.value.trim();
    }
    
    // Statuses: an enabled field must hold a canonical value. Refuse the whole save rather than write
    // an off-enum value — or silently skip it — across every selected session (KB-4101).
    const signupValue = formData.signupStatus.value;
    const availabilityValue = formData.availabilityStatus.value;
    if (
      (formData.signupStatus.enabled && signupValue && !isSignupStatus(signupValue)) ||
      (formData.availabilityStatus.enabled && availabilityValue && !isAvailabilityStatus(availabilityValue))
    ) {
      toast.error("Not saved: pick a registration and availability status from the list.");
      return;
    }
    if (formData.signupStatus.enabled && isSignupStatus(signupValue)) {
      updateData.signupStatus = signupValue;
    }
    if (formData.availabilityStatus.enabled && isAvailabilityStatus(availabilityValue)) {
      updateData.availabilityStatus = availabilityValue;
    }

    // Add session description to update data if enabled
    if (formData.sessionDescription.enabled) {
      updateData.description = formData.sessionDescription.value;
    }
    
    // Add session URL to update data if enabled
    if (formData.sessionUrl.enabled) {
      updateData.url = (formData.sessionUrl.value || "").trim();
    }
    
    // Add registration URL to update data if enabled
    if (formData.registrationUrl.enabled) {
      updateData.regUrl = (formData.registrationUrl.value || "").trim();
    }
    
    // Add price note to update data if enabled
    if (formData.priceNote.enabled) {
      updateData.priceNoteOverride = formData.priceNote.value;
    }
    
    // Add timing note to update data if enabled
    if (formData.timingNote.enabled) {
      updateData.timingNoteOverride = formData.timingNote.value;
    }
    
    // Add visibility to update data if enabled
    if (formData.visibility.enabled && formData.visibility.value) {
      updateData.visibility = formData.visibility.value;
    }

    // Overwrite/wipe registration events if enabled
    if (formData.regEvents?.enabled) {
      const raw = (formData.regEvents.value || []) as any[];
      const validEvents = raw.filter((e) => e && e.when !== undefined && e.when !== null);
      // If user enabled this field but left it blank, wipe regEvents
      updateData.regEvents = validEvents.length > 0 ? (validEvents as any) : null;
    }
    
    // Get the price values if price fields are enabled
    let priceValue: number | null = null;
    let salePriceValue: number | null = null;
    
    if (formData.regularPrice.enabled) {
      priceValue = formData.regularPrice.value === "" ? null : Number(formData.regularPrice.value);
    }
    
    if (formData.currentSalePrice.enabled) {
      salePriceValue = formData.currentSalePrice.value === "" ? null : Number(formData.currentSalePrice.value);
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading(`Updating ${selectedOffers.length} sessions...`);

      // Group offers by their variation's regular price to handle updates correctly
      const offersByVariationPrice: { [key: string]: { id: string; data: Partial<Offer> }[] } = {};

      // Prepare all updates first, grouping by variation price
      selectedOffers.forEach(offer => {
        const offerUpdate = { ...updateData };
        const variationRegularPrice = offer.variationData?.priceRegular;
        const priceKey = `${variationRegularPrice}`;
        
        // Handle price fields if price is enabled
        if (formData.regularPrice.enabled) {
          // Check if the entered price matches THIS variation's regular price or is empty
          if (formData.regularPrice.value === "" || priceValue === variationRegularPrice) {
            // If price is empty or matches variation's regular price, set priceOverride to null
            offerUpdate.priceOverride = null;
            // Always set priceRegular to variation's regular price when priceOverride is null
            offerUpdate.priceRegular = variationRegularPrice;
          } else {
            // Otherwise use the entered price as override for THIS session
            offerUpdate.priceOverride = priceValue;
            // When using priceOverride, set priceRegular to the same value
            offerUpdate.priceRegular = priceValue;
          }
        }
        
        // Handle current sale price if enabled
        if (formData.currentSalePrice.enabled) {
          offerUpdate.currentSalePrice = salePriceValue;
        }

        // Calculate priceCalc following strict precedence rules:
        // 1. If currentSalePrice is being set in this update, use that
        // 2. If currentSalePrice is not being set but exists on offer, use existing currentSalePrice
        // 3. If no currentSalePrice (new or existing), use priceOverride from this update if being set
        // 4. If no priceOverride being set, use existing priceOverride
        // 5. Finally, fall back to variationData.priceRegular
        let calculatedPrice: number | null = null;

        // First check if currentSalePrice is being set or exists
        if (formData.currentSalePrice.enabled) {
          calculatedPrice = salePriceValue;
        } else if (offer.currentSalePrice !== null && offer.currentSalePrice !== undefined) {
          calculatedPrice = offer.currentSalePrice;
        }
        // If no currentSalePrice, check priceOverride
        else if (formData.regularPrice.enabled) {
          calculatedPrice = offerUpdate.priceOverride !== null ? offerUpdate.priceOverride : offerUpdate.priceRegular;
        } else if (offer.priceOverride !== null && offer.priceOverride !== undefined) {
          calculatedPrice = offer.priceOverride;
        }
        // Finally, fall back to variation regular price
        else {
          calculatedPrice = variationRegularPrice ?? null;
        }

        offerUpdate.priceCalc = calculatedPrice;

        // Calculate pricePerDayCalc if we have priceCalc and numDaysCalc
        if (calculatedPrice !== null && offer.numDaysCalc && offer.numDaysCalc > 0) {
          offerUpdate.pricePerDayCalc = calculatedPrice / offer.numDaysCalc;
        }

        // Add to the appropriate group
        if (!offersByVariationPrice[priceKey]) {
          offersByVariationPrice[priceKey] = [];
        }
        offersByVariationPrice[priceKey].push({ id: offer.id, data: offerUpdate });
      });

      // Process each group of offers separately
      for (const [_, updates] of Object.entries(offersByVariationPrice)) {
        const validUpdates = updates.filter(update => Object.keys(update.data).length > 0);
        
        if (validUpdates.length > 0) {
          // Perform updates for this group, suppressing individual toasts
          await onBulkUpdateOffers(
            validUpdates.map(u => u.id),
            validUpdates[0].data, // All updates in this group have the same data
            true // Suppress toasts for individual group updates
          );
        }
      }

      // Dismiss loading toast and show final status
      toast.dismiss(loadingToast);
      const totalUpdated = Object.values(offersByVariationPrice).reduce((sum, group) => 
        sum + group.filter(update => Object.keys(update.data).length > 0).length, 0);
      
      if (totalUpdated > 0) {
        toast.success(`Successfully updated ${totalUpdated} sessions`);
      } else {
        toast.error("No sessions were updated");
      }

      // Close modal and refresh data
      setIsBulkEditModalOpen(false);
      if (onRefreshOffers) {
        await onRefreshOffers();
      }
    } catch (error) {
      console.error("Error in bulk update:", error);
      toast.error("Failed to update sessions");
    }
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };
  
  // Handle page size change
  const handlePageSizeChange = (value: string) => {
    setPageSize(value);
  };

  return {
    selectedOffers,
    setSelectedOffers,
    searchTerm,
    setSearchTerm,
    pageSize,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    offerToDelete,
    isBulkEditModalOpen,
    setIsBulkEditModalOpen,
    // Add new bulk delete dialog state
    isBulkDeleteDialogOpen,
    setIsBulkDeleteDialogOpen,
    handleRowSelect,
    handleDeleteClick,
    handleConfirmDelete,
    handleBulkDelete,
    handleConfirmBulkDelete, // Add the new bulk delete confirmation handler
    handleBulkEdit,
    handleSaveBulkEdit,
    handleSearchChange,
    handlePageSizeChange
  };
};
